'use client';

import {
  CURRENCY_ACCESSIBLE_LABEL,
  CURRENCY_LABEL,
  CURRENCY_SHORT_LABEL,
  REHUB_CURRENCIES,
  persistBillingCurrency,
  type RehubCurrency,
} from '@rehub/pricing';

export interface CurrencySelectorProps {
  value: RehubCurrency;
  onChange: (currency: RehubCurrency) => void;
  currencies?: readonly RehubCurrency[];
  disabled?: boolean;
  /** Subtle "Currency" label above the pills. */
  showLabel?: boolean;
  className?: string;
  /** Selected-state classes. Defaults suit dark HDR/marketing panels. */
  activeClassName?: string;
  inactiveClassName?: string;
}

/**
 * Compact three-way currency toggle: UK £ | EU € | US $.
 * On narrow screens the regional prefix is hidden so £ | € | $ still fit.
 */
export default function CurrencySelector({
  value,
  onChange,
  currencies = REHUB_CURRENCIES,
  disabled = false,
  showLabel = false,
  className = '',
  activeClassName = 'bg-sky-600 text-white',
  inactiveClassName = 'text-slate-400 hover:text-slate-200',
}: CurrencySelectorProps) {
  const switchCurrency = (next: RehubCurrency) => {
    if (disabled || next === value) return;
    persistBillingCurrency(next);
    onChange(next);
  };

  return (
    <div className={className}>
      {showLabel && (
        <p className="mb-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">
          Currency
        </p>
      )}
      <div
        role="group"
        aria-label="Currency"
        className="flex rounded-full border border-slate-700/60 p-1"
      >
        {currencies.map((code) => (
          <button
            key={code}
            type="button"
            disabled={disabled}
            aria-pressed={value === code}
            aria-label={CURRENCY_ACCESSIBLE_LABEL[code]}
            title={CURRENCY_ACCESSIBLE_LABEL[code]}
            onClick={() => switchCurrency(code)}
            className={`rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors sm:px-3.5 sm:py-2 sm:text-sm ${
              value === code ? activeClassName : inactiveClassName
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <span className="sm:hidden">{CURRENCY_SHORT_LABEL[code]}</span>
            <span className="hidden sm:inline">{CURRENCY_LABEL[code]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
